import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../data/volumePages';
type PageLightboxProps = {
  pages: VolumePage[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
};
export function PageLightbox({
  pages,
  index,
  onClose,
  onIndexChange
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const open = index !== null;
  const last = pages.length - 1;

  // Jump to the tapped page instantly when opening.
  useLayoutEffect(() => {
    if (!open) return;
    const track = trackRef.current;
    if (track) track.scrollLeft = (index ?? 0) * track.clientWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') scrollToIndex((index ?? 0) + 1, 'auto');
      if (e.key === 'ArrowLeft') scrollToIndex((index ?? 0) - 1, 'auto');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const scrollToIndex = (i: number, behavior: ScrollBehavior = 'smooth') => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(last, i));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: reduce ? 'auto' : behavior
    });
  };
  const onScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    if (i !== index) onIndexChange(i);
  };
  const navClass = 'absolute top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-30 disabled:hover:bg-white/10 lg:flex';
  const current = index !== null ? pages[index] : null;
  return <AnimatePresence data-id="element-168" data-mp="w6fqoeoax6li">
      {open && current && <motion.div role="dialog" aria-modal="true" aria-label={`Page preview: ${current.label}`} className="fixed inset-0 z-50 bg-black/90" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-169" data-mp="w7923sy7gk9z">
          <ul ref={trackRef} onScroll={onScroll} className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-170" data-mp="2b2qzvboc57ki">
            {pages.map((page, i) => <li key={page.src} className="flex h-full w-full flex-none snap-center items-center justify-center px-4 pb-14 pt-16" onClick={onClose} aria-hidden={i !== index} data-id="element-171" data-mp="263o0a6r4jtjx" data-mpi={i}>
                <img src={page.src} alt={page.alt} loading={Math.abs(i - (index ?? 0)) <= 1 ? 'eager' : 'lazy'} onClick={e => e.stopPropagation()} className="max-h-full w-auto max-w-full rounded-xl object-contain" data-id="element-172" data-mp="2yrtfyf3bmscw" />
              </li>)}
          </ul>

          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close page preview" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-173" data-mp="3hk4druj4c045">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-174" data-mp="1eysahn7pq6pq" />
          </button>

          <button type="button" onClick={() => scrollToIndex((index ?? 0) - 1)} disabled={index === 0} aria-label="Previous page" className={`${navClass} left-6`} data-id="element-175" data-mp="2cliotqjijh51">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-176" data-mp="22tmos9xv9gy6" />
          </button>
          <button type="button" onClick={() => scrollToIndex((index ?? 0) + 1)} disabled={index === last} aria-label="Next page" className={`${navClass} right-6`} data-id="element-177" data-mp="1xvtjrk8auszh">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-178" data-mp="2qj7b92t8awc7" />
          </button>

          <p className="pointer-events-none absolute inset-x-0 bottom-4 px-4 text-center text-sm text-white/80" aria-live="polite" data-id="element-179" data-mp="2yvqze9qiz23n">
            {(index ?? 0) + 1} / {pages.length} · {current.caption}
          </p>
        </motion.div>}
    </AnimatePresence>;
}